import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { forkJoin } from 'rxjs';
import { catchError, concatMap, map, mergeMap } from 'rxjs/operators';
import { DateTimeHelper } from '../../../features/calendar/util/date-time-helper';
import { HttpOperation } from '../../enums/http-operation';
import { TodoEntryModel } from '../../models/todo-planner/todo-entry.model';
import { EndpointService } from '../../services/endpoints/endpoint.service';
import { HttpErrorService } from '../../services/http-error.service';
import * as fromTodoPlannerEntryActions from './todo-planner.actions';

@Injectable()
export class TodoPlannerEntriesEffects {
  fetchTodoPlannerEntries$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTodoPlannerEntryActions.fetchTodoPlannerEntries),
      map((params) => params.payload),
      mergeMap((timeBlockRequestParams) => {
        const startStr = DateTimeHelper.format(timeBlockRequestParams.start as Date);
        const endStr = DateTimeHelper.format(timeBlockRequestParams.end as Date);
        const userId = timeBlockRequestParams.userId;

        let params = new HttpParams();

        params = startStr ? params.append('start_date', startStr) : params;
        params = endStr ? params.append('end_date', endStr) : params;
        params = params.append('user_id', userId);

        const endpoint = this.endpointService.getTodoPlannerSlug(HttpOperation.Get);

        return this.http.get<unknown>(endpoint, { params }).pipe(
          map((json: unknown[]) => {
            const entries = plainToInstance(TodoEntryModel, json);
            return fromTodoPlannerEntryActions.setTodoPlannerEntries({ payload: entries });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'TodoPlannerEntries',
              fromTodoPlannerEntryActions.fetchTodoPlannerEntries,
              fromTodoPlannerEntryActions,
              error,
            );
          }),
        );
      }),
    );
  });

  fetchSingleTodoPlannerEntry$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTodoPlannerEntryActions.fetchSingleTodoPlannerEntry),
      map((action) => action.payload),
      concatMap((todoEntryId) => {
        const endpoint = this.endpointService.getTodoPlannerSlug(HttpOperation.Get, todoEntryId);

        return this.http.get<unknown>(endpoint).pipe(
          map((json: unknown) => {
            const entry = plainToInstance(TodoEntryModel, json) as TodoEntryModel;
            return fromTodoPlannerEntryActions.setSingleTodoPlannerEntry({ payload: entry });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'TodoPlannerEntries',
              fromTodoPlannerEntryActions.fetchSingleTodoPlannerEntry,
              fromTodoPlannerEntryActions,
              error,
            );
          }),
        );
      }),
    );
  });

  addTodoPlannerEntry$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTodoPlannerEntryActions.addTodoPlannerEntry),
      map((action) => action.payload),
      mergeMap((entryModel) => {
        const endpoint = this.endpointService.getTodoPlannerSlug(HttpOperation.Post);
        const httpPlannerEntry = instanceToPlain(entryModel);
        return this.http.post<unknown>(endpoint, httpPlannerEntry).pipe(
          map((json: unknown) => {
            const entry = plainToInstance(TodoEntryModel, json) as TodoEntryModel;
            return fromTodoPlannerEntryActions.todoPlannerEntryAdded({
              payload: { todoEntryModel: entry },
            });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'TodoPlannerEntries',
              fromTodoPlannerEntryActions.addTodoPlannerEntry,
              fromTodoPlannerEntryActions,
              error,
            );
          }),
        );
      }),
    );
  });

  updateTodoPlannerEntry$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTodoPlannerEntryActions.updateTodoPlannerEntry),
      map((action) => action.payload),
      mergeMap((payload) => {
        const todoEntryModel = payload.todoEntryModel;

        const endpoint = this.endpointService.getTodoPlannerSlug(
          HttpOperation.Put,
          todoEntryModel.id,
        );
        const httpPlannerEntry = instanceToPlain(todoEntryModel);

        return this.http.put<unknown>(endpoint, httpPlannerEntry).pipe(
          map((json: unknown) => {
            const updatedModel = plainToInstance(TodoEntryModel, json) as TodoEntryModel;
            updatedModel.reRenderComponent = todoEntryModel.reRenderComponent;
            return fromTodoPlannerEntryActions.todoPlannerEntryUpdated({
              payload: { todoEntryModel: updatedModel },
            });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'TodoPlannerEntries',
              fromTodoPlannerEntryActions.updateTodoPlannerEntry,
              fromTodoPlannerEntryActions,
              error,
            );
          }),
        );
      }),
    );
  });

  updateMultipleTodoPlannerEntries$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTodoPlannerEntryActions.updateMultipleTodoPlannerEntries),
      map((action) => action.payload),
      mergeMap((payload) => {
        const todoEntryModels = payload.todoEntryModels;
        const requests$ = todoEntryModels.map((todoEntryModel) => {
          const endpoint = this.endpointService.getTodoPlannerSlug(
            HttpOperation.Put,
            todoEntryModel.id,
          );

          const httpPlannerEntry = instanceToPlain(todoEntryModel);

          return this.http.put<unknown>(endpoint, httpPlannerEntry).pipe(
            map((json: unknown) => {
              const updatedModel = plainToInstance(TodoEntryModel, json) as TodoEntryModel;

              updatedModel.reRenderComponent = todoEntryModel.reRenderComponent;
              return updatedModel;
            }),
          );
        });

        return forkJoin(requests$).pipe(
          map((updatedModels: TodoEntryModel[]) =>
            fromTodoPlannerEntryActions.todoPlannerMultipleEntriesUpdated({
              payload: { todoEntryModels: updatedModels },
            }),
          ),
          catchError((error) =>
            this.httpErrorService.handleError(
              'TodoPlannerEntries',
              fromTodoPlannerEntryActions.updateMultipleTodoPlannerEntries,
              fromTodoPlannerEntryActions,
              error,
            ),
          ),
        );
      }),
    );
  });

  deleteTodoPlannerEntry$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTodoPlannerEntryActions.deleteTodoPlannerEntry),
      map((action) => action.payload),
      mergeMap((entry) => {
        const endpoint = this.endpointService.getTodoPlannerSlug(HttpOperation.Delete, entry.id);

        return this.http.delete<unknown>(endpoint).pipe(
          map(() => {
            return fromTodoPlannerEntryActions.todoPlannerEntryDeleted({
              payload: { todoEntryModel: entry },
            });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'TodoPlannerEntries',
              fromTodoPlannerEntryActions.deleteTodoPlannerEntry,
              fromTodoPlannerEntryActions,
              error,
            );
          }),
        );
      }),
    );
  });

  constructor(
    private readonly actions$: Actions,
    private readonly http: HttpClient,
    private readonly endpointService: EndpointService,
    private readonly httpErrorService: HttpErrorService,
  ) {}
}
